/**
 * LabelVerify — audit-trail page for a single printed food label.
 *
 * This route is intentionally PUBLIC (mounted outside <PrivateRoute>) so
 * the printed QR deep-link works for any HACCP inspector with a phone
 * camera — no login, no app install. Two access paths:
 *
 *   /labels/verify?id=<label_uuid>        Public flow (hit via printed QR
 *                                         from a phone camera). The UUID
 *                                         is the bearer token. Calls the
 *                                         /api/labels/public/{id} route.
 *
 *   /labels/verify?batch=<batch>          In-house authed flow (kitchen
 *                                         staff scanning via QuickScan).
 *                                         Calls /api/labels/by-batch with
 *                                         the active site context.
 *
 * Rendering also branches on auth:
 *   - When the user has a session, we keep the regular surface look so
 *     the sidebar/topbar of <AppLayout> still wraps the page (the route
 *     is mounted at the top level, but in-house users will navigate here
 *     through the menu — we conditionally render an inner page header
 *     only).
 *   - Anonymous viewers see a stripped public chrome with HACCP AU
 *     branding so the page stands on its own when it's the first thing
 *     a phone camera opens.
 */
import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import api from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import PublicShell from "@/components/PublicShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle2, ScanLine, ArrowLeft, Clock, Snowflake } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-AU"); } catch { return iso; }
}
function isExpired(iso) {
  if (!iso) return false;
  try { return new Date(iso) < new Date(); } catch { return false; }
}

export default function LabelVerify() {
  const { activeSite } = useSite();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  // Public path: the QR carries `id=<uuid>` plus GS1 AIs (10/17/20/91).
  // Authed path: QuickScan passes `batch=<batch>` and relies on the
  // session's activeSite for tenant scoping.
  const labelId = params.get("id") || "";
  const batch = params.get("batch") || params.get("10") || "";

  const isPublicView = !!labelId;        // QR-scan flow
  const isAuthedBatchView = !labelId && !!batch;

  const [state, setState] = useState({ loading: true, label: null, breaches: [], site: null, error: "" });

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: "" }));
    (async () => {
      try {
        if (isPublicView) {
          // Public — no auth, no site context needed; the UUID is the
          // bearer. Use raw axios so the auth-aware interceptor in
          // api.js doesn't redirect us to /login on a 401 (the endpoint
          // never 401s, but defensive).
          const url = `${API_BASE.replace(/\/+$/, "")}/api/labels/public/${encodeURIComponent(labelId)}`;
          const { data } = await axios.get(url);
          if (cancelled || !data) return;
          setState({
            loading: false,
            label: data.label,
            breaches: data.breaches_since_prep || [],
            site: data.site || null,
            error: "",
          });
          return;
        }
        if (isAuthedBatchView && activeSite) {
          const { data } = await api.get("/labels/by-batch", {
            params: { batch, site_id: activeSite.id },
          });
          if (cancelled || !data) return;
          setState({
            loading: false,
            label: data.label,
            breaches: data.breaches_since_prep || [],
            site: null,
            error: "",
          });
        }
      } catch (err) {
        if (cancelled) return;
        const detail = err?.response?.data?.detail || err.message || "Lookup failed";
        setState({
          loading: false, label: null, breaches: [], site: null,
          error: typeof detail === "string" ? detail : "Lookup failed",
        });
      }
    })();
    return () => { cancelled = true; };
  }, [labelId, batch, activeSite, isPublicView, isAuthedBatchView]);

  // --- Guard rails for missing query params or context -----------------
  if (!labelId && !batch) {
    return (
      <PublicShell>
        <div className="surface p-8 text-center text-charcoal-soft">
          No batch number provided.{" "}
          {user ? (
            <Link to="/scan" className="text-ochre-600 underline">Open scanner</Link>
          ) : (
            <span>Scan a printed label's QR code to look it up.</span>
          )}
        </div>
      </PublicShell>
    );
  }

  if (isAuthedBatchView && !activeSite) {
    return (
      <PublicShell title="Verify label" subtitle={`Audit trail for batch ${batch}`}>
        <div className="surface p-8 text-center" data-testid="lv-no-site">
          <AlertTriangle className="w-8 h-8 text-ochre-600 mx-auto mb-2" />
          <div className="font-display font-bold text-lg">Select a site first</div>
          <p className="text-sm text-charcoal-soft mt-1 max-w-md mx-auto">
            Pick a site from the selector in the top bar so we can look up batch{" "}
            <span className="font-mono">{batch}</span> against the correct cold-chain history.
          </p>
        </div>
      </PublicShell>
    );
  }

  // --- Main render ----------------------------------------------------
  const subtitle = isPublicView
    ? `Audit trail · ${state.site?.name ? state.site.name : "Verified record"}`
    : `Audit trail for batch ${batch}`;

  const headerActions = user ? (
    <div className="flex gap-2">
      <Button
        data-testid="lv-rescan"
        variant="outline"
        onClick={() => navigate("/scan")}
        className="h-10 gap-2 border-eucalyptus-700 text-eucalyptus-900"
      >
        <ScanLine className="w-4 h-4" /> Scan another
      </Button>
      <Button
        data-testid="lv-back"
        variant="ghost"
        onClick={() => navigate(-1)}
        className="h-10 gap-2"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </Button>
    </div>
  ) : null;

  return (
    <PublicShell title="Verify label" subtitle={subtitle} action={headerActions} badge={isPublicView}>
      <div data-testid="label-verify-page">
      {state.loading && (
        <div className="surface p-8 text-center text-charcoal-soft">Looking up label…</div>
      )}

      {!state.loading && state.error && (
        <div className="surface p-8 text-center" data-testid="lv-error">
          <AlertTriangle className="w-8 h-8 text-ochre-600 mx-auto mb-2" />
          <div className="font-display font-bold text-lg">{state.error}</div>
          <p className="text-sm text-charcoal-soft mt-1">
            This label may have been printed at a different site or before our records start.
          </p>
        </div>
      )}

      {!state.loading && state.label && (
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 surface p-6" data-testid="lv-label-card">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="label-eyebrow">{state.label.storage?.replace("_", " ")}</div>
                <h2 className="font-display font-extrabold text-2xl mt-1" data-testid="lv-product">
                  {state.label.product_name}
                </h2>
                {state.label.quantity && (
                  <div className="text-sm text-charcoal-soft mt-1">{state.label.quantity}</div>
                )}
              </div>
              {isExpired(state.label.use_by_date) ? (
                <Badge className="bg-ochre-600 text-white hover:bg-ochre-600 gap-1" data-testid="lv-expired">
                  <Clock className="w-3.5 h-3.5" /> EXPIRED
                </Badge>
              ) : (
                <Badge className="bg-eucalyptus-100 text-eucalyptus-900 hover:bg-eucalyptus-100 gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Within use-by
                </Badge>
              )}
            </div>

            <dl className="grid sm:grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="label-eyebrow">Batch</dt>
                <dd className="font-mono mt-1" data-testid="lv-batch">{state.label.batch_number}</dd>
              </div>
              <div>
                <dt className="label-eyebrow">Prep</dt>
                <dd className="font-mono mt-1">{fmt(state.label.prep_date)}</dd>
              </div>
              <div>
                <dt className="label-eyebrow">Use-by</dt>
                <dd className="font-mono mt-1 font-bold" data-testid="lv-useby">{fmt(state.label.use_by_date)}</dd>
              </div>
              <div>
                <dt className="label-eyebrow">Printed by</dt>
                <dd className="mt-1" data-testid="lv-cook" title="The user who pressed Print on this label — not necessarily the cook who prepared the food.">
                  {state.label.user_name || "—"}
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="label-eyebrow">Allergens</dt>
                <dd className="mt-1 uppercase font-mono text-xs" data-testid="lv-allergens">
                  {state.label.allergens?.length ? state.label.allergens.join(", ") : "None recorded"}
                </dd>
              </div>
              {state.label.notes && (
                <div className="sm:col-span-2">
                  <dt className="label-eyebrow">Notes</dt>
                  <dd className="mt-1 text-sm">{state.label.notes}</dd>
                </div>
              )}
            </dl>
          </div>

          <div className="surface p-6" data-testid="lv-breaches-card">
            <div className="flex items-center gap-2 mb-3">
              <Snowflake className="w-5 h-5 text-eucalyptus-700" />
              <h3 className="font-display font-bold">Breaches since prep</h3>
            </div>
            {state.breaches.length === 0 ? (
              <div className="text-sm text-eucalyptus-700 flex items-center gap-1" data-testid="lv-no-breaches">
                <CheckCircle2 className="w-4 h-4" /> No temperature breaches at this site since the batch was prepared.
              </div>
            ) : (
              <ul className="space-y-3" data-testid="lv-breaches-list">
                {state.breaches.map((b) => (
                  <li key={b.id} className="border-t border-[color:var(--border)] pt-2 first:border-0 first:pt-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-sm font-bold">{b.equipment_name}</span>
                      <Badge className="bg-ochre-600 text-white hover:bg-ochre-600 font-mono">
                        {b.temperature_c}°
                      </Badge>
                    </div>
                    <div className="text-xs text-charcoal-soft mt-1 font-mono">
                      {fmt(b.created_at)} · {b.user_name}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
      </div>
    </PublicShell>
  );
}
